import { create } from 'zustand';

import RelationController, { ILikesResponse } from '@/api/relation/relation.ts';
import { Contact as RelationContact } from '@/api/relation/types.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';

interface LikesSentListStore {
  sentLikes: RelationContact[];
  pageToken: string | undefined;
  isFetching: boolean;
  loadSentLikes: (pageToken?: string) => Promise<void>;
  removeSentLike: (hid: string) => void;
  reset: () => void;
}

const SENT_LIKES_LIMIT = 100;

export const useLikesSentListStore = create<LikesSentListStore>((set, get) => ({
  sentLikes: [],
  pageToken: undefined,
  isFetching: false,
  loadSentLikes: async (pageToken) => {
    try {
      const { isFetching } = get();

      if (isFetching) return;

      set({ isFetching: true });

      await new Promise((resolve) => setTimeout(resolve, 250));

      let newSentLikes: ILikesResponse;

      if (pageToken) {
        newSentLikes = await RelationController.getLikesSent(
          SENT_LIKES_LIMIT,
          pageToken
        );
      } else {
        newSentLikes = await RelationController.getLikesSent(SENT_LIKES_LIMIT);
      }

      const pToken =
        newSentLikes.next_page_token.length !== 0
          ? newSentLikes.next_page_token
          : undefined;

      set((prev) => {
        const mergedLikes = pageToken
          ? [...prev.sentLikes, ...newSentLikes.contacts]
          : [...newSentLikes.contacts, ...prev.sentLikes];

        const uniqueLikesMap = new Map<string, RelationContact>();

        for (const like of mergedLikes) {
          if (!uniqueLikesMap.has(like.profile.hid)) {
            uniqueLikesMap.set(like.profile.hid, like);
          }
        }

        return {
          sentLikes: Array.from(uniqueLikesMap.values()),
          pageToken: pToken,
          isFetching: false,
          isTabActive: true
        };
      });
    } catch (e) {
      catchErrors(e, true);
      set({ isFetching: false });
    }
  },
  removeSentLike: (hid) => {
    const currentSentLikes = get().sentLikes;
    set({
      sentLikes: currentSentLikes.filter(
        (sentLike) => sentLike.profile.hid !== hid
      )
    });
  },
  reset: () => {
    set({
      sentLikes: [],
      pageToken: undefined,
      isFetching: false
    });
  }
}));
